import { FC, useEffect, useMemo } from 'react';

import { ForWhom, RegistrationCandidateDetails } from 'store/candidate/types';
import { GenderOptions, MALAYSIA_COUNTRY_ID, NativeLangOptions } from 'core';
import { BcCheckbox, ReadonlyField } from 'components';
import { useGetAllCountriesQuery } from 'store/country/countryAPI';
import { useGetCustomNotificationQuery, useGetTaxRateQuery } from 'store/organisation-country/organisation-country-API';
import { useAppSelector } from 'store';
import { selectSelectedCountry } from 'store/country/countrySlice';
import styled from '@emotion/styled';

import { DateField, PageLoader, RadioGroup, SelectField, TextField } from '@britishcouncil/react-solas-ors3';
import { BcAlert } from '@britishcouncil/react-solas';
import { FormFields } from '@britishcouncil/react-forms';
import { IsMalaysianCitizenOptions } from './consts';
import { MalaysianCitizenStatus } from 'core/const/MalaysianCitizenStatus';

const RussiaCountryId = 78;

export const CandidateFields: FC<{
  fields: FormFields<RegistrationCandidateDetails>;
  showPanNumber?: boolean;
  showTaxNumber?: boolean;
}> = ({ fields, showPanNumber, showTaxNumber }) => {
  const { data: countries } = useGetAllCountriesQuery();

  const selectedCountry = useAppSelector(selectSelectedCountry);

  const { data: customNotificationData, isFetching: customNotificationFetching } = useGetCustomNotificationQuery({
    countryId: selectedCountry!.id, // country always is selected on that page
  });
  const { data: taxRateData } = useGetTaxRateQuery({ countryId: selectedCountry!.id });

  const isRussia = Number(fields.countryId.value) === RussiaCountryId;

  const shouldShowMalaysianCitizenQuestion = useMemo(() => {
    return taxRateData && taxRateData.taxRate > 0 && selectedCountry!.id === MALAYSIA_COUNTRY_ID;
  }, [taxRateData, selectedCountry]);

  useEffect(() => {
    if (!fields.hasFamilyName.value) {
      fields.lastName.onChange(null);
    } else if (!fields.lastName.value) {
      fields.lastName.onChange('');
    }
  }, [fields.hasFamilyName.value, fields.lastName, fields.lastName.value]);

  return (
    <>
      {fields.forWhom.value !== ForWhom.Myself && <h2>Minor candidate details:</h2>}

      {customNotificationFetching && <PageLoader />}
      {customNotificationData && (
        <BcAlert type="note" title={customNotificationData.noticeTitle}>
          <p>{customNotificationData.noticeDetails}</p>
        </BcAlert>
      )}

      {showTaxNumber && <TextField label="Customer tax registration number" {...fields.taxNumber} showColon={false} />}
      {showPanNumber && <TextField label="Permanent account number (PAN)" {...fields.panNumber} showColon={false} />}
      <TextField label="Candidate first name(s) *" {...fields.firstName} showColon={false} />
      <TextField
        label="Candidate last name"
        {...fields.lastName}
        showColon={false}
        disabled={!fields.hasFamilyName.value}
        value={fields.lastName.value ?? ''}
      />
      <S.CheckBoxContainer>
        <BcCheckbox
          {...fields.hasFamilyName}
          value={!fields.hasFamilyName.value}
          onChange={(val) => fields.hasFamilyName.onChange(!val)}
        >
          I do not have a last name
        </BcCheckbox>
      </S.CheckBoxContainer>

      {fields.forWhom.value === ForWhom.Myself && <ReadonlyField label="Email">{fields.email.value}</ReadonlyField>}
      {fields.forWhom.value === ForWhom.NewChild ? (
        <DateField label="Date of birth *" mode="dob" {...fields.dateOfBirth} showColon={false} />
      ) : (
        <ReadonlyField label="Date of birth">{fields.dateOfBirth.value}</ReadonlyField>
      )}
      <label>Gender *</label>
      <RadioGroup {...fields.gender} radios={GenderOptions}></RadioGroup>
      <BcAlert type="note" title="Confirmation">
        <p>
          <BcCheckbox {...fields.confirmationSelected}>
            Please make sure that selected/entered candidate name(s) and date of birth are accurate and the same as they
            appear in candidate identification document
          </BcCheckbox>
        </p>
      </BcAlert>

      {shouldShowMalaysianCitizenQuestion && (
        <>
          <label htmlFor="isMalaysianCitizen">Are you a Malaysian citizen? *</label>
          <RadioGroup
            {...fields.isMalaysianCitizen}
            value={`${fields.isMalaysianCitizen.value}`}
            onChange={(val) =>
              fields.isMalaysianCitizen.onChange(
                val === MalaysianCitizenStatus.Yes ? MalaysianCitizenStatus.Yes : MalaysianCitizenStatus.No,
              )
            }
            radios={IsMalaysianCitizenOptions}
          ></RadioGroup>
        </>
      )}

      <SelectField
        label="English native language*"
        {...fields.nativeLanguage}
        options={NativeLangOptions}
        showColon={false}
      />
      <TextField label="Address line 1 *" {...fields.streetAddress1} showColon={false} />
      <TextField label="Address line 2" {...fields.streetAddress2} showColon={false} />
      <TextField label="Address line 3" {...fields.streetAddress3} showColon={false} />
      <TextField label="Address line 4" {...fields.streetAddress4} showColon={false} />
      <TextField label="City *" {...fields.city} showColon={false} />
      <TextField label="Post code" {...fields.postCode} showColon={false} />
      <SelectField label="Country/Territory *" {...fields.countryId} options={countries || []} showColon={false} />

      {isRussia && (
        <BcAlert type="note" title="Important Notice">
          <p>
            Please be informed that the British Council has been included in the register of “undesirable organisations”
            in Russia. This is related to a change in Russian legislation that impacts organisations linked to foreign
            governments that are viewed as undesirable by the Russian government. Any connection with the British
            Council could potentially entail risks for individuals who are Russian citizens or have other ties to
            Russia. This applies even if the individuals live outside of Russia. We encourage you to consider this
            situation carefully and assess personal risks.
          </p>
        </BcAlert>
      )}

      {fields.forWhom.value === ForWhom.Myself && (
        <>
          <TextField label="Mobile number *" {...fields.mobile} showColon={false} />
          <TextField label="Alternative contact number" {...fields.telephone} showColon={false} />
        </>
      )}
    </>
  );
};

const S = {
  CheckBoxContainer: styled.div`
    .checkbox {
      label {
        padding: 0 !important;

        input {
          position: relative;
          top: 0;
          left: 0;
          margin-right: 32px;
          margin-left: 12px;
        }
      }
    }
  `,
};
